import { captureException } from "@sentry/react";
import { ReactNode, useCallback } from "react";
import { toast, type ExternalToast } from "sonner";

const DEFAULT_DURATION = 3000;

type ToastProps = ExternalToast & {
  message?: string;
  title?: string | ReactNode;
};

export const useToast = () => {
  const error = useCallback(
    (
      {
        message = "Please try again",
        title = "Error",
        duration = DEFAULT_DURATION,
        ...rest
      }: ToastProps,
      err?: object
    ) => {
      if (err)
        captureException(err instanceof Error ? err : new Error(String(err)));
      return toast.error(title, { description: message, duration, ...rest });
    },
    []
  );

  const success = ({
    message,
    title = "Successful",
    duration = DEFAULT_DURATION,
    ...rest
  }: ToastProps) =>
    toast.success(title, { description: message, duration, ...rest });

  const info = ({
    message,
    title = "Info",
    duration = DEFAULT_DURATION,
    ...rest
  }: ToastProps) =>
    toast.info(title, { description: message, duration, ...rest });

  const warning = ({
    message,
    title = "Warning",
    duration = DEFAULT_DURATION,
    ...rest
  }: ToastProps) =>
    toast.warning(title, { description: message, duration, ...rest });

  const loading = ({
    message,
    title = "Loading...",
    id = crypto.randomUUID(),
    ...rest
  }: ToastProps) => toast.loading(title, { description: message, id, ...rest });

  const promise = <T>(
    promiseOrFn: Promise<T> | (() => Promise<T>),
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: unknown) => string | undefined);
    }
  ) =>
    toast.promise(
      typeof promiseOrFn === "function" ? promiseOrFn() : promiseOrFn,
      {
        ...options,
        error: (err) => {
          captureException(err);
          const msg =
            typeof options.error === "function"
              ? options.error(err)
              : options.error;
          return msg || "Please try again";
        },
      }
    );

  return {
    alertToast: error,
    successToast: success,
    loading,
    info,
    warning,
    promise,
    dismiss: toast.dismiss,
    success,
    error,
    toast,
  };
};
